import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, DownloadCloudIcon, UploadCloudIcon, MapIcon, LibraryIcon, ArrowRightIcon, FileSpreadsheetIcon } from 'lucide-react';
import { useDarkClasses } from '../../../hooks/useDarkClasses';
import { ImportMethod } from './types';
import { GoodreadsImportForm } from './GoodreadsImportForm';
import { UniversalCsvForm } from './UniversalCsvForm';
import { GenericCsvForm } from './GenericCsvForm';
import { ImportPreviewQueue } from './ImportPreviewQueue';
export default function BookshelfImport() {
  const dk = useDarkClasses();
  const navigate = useNavigate();
  const [activeMethod, setActiveMethod] = useState<ImportMethod>('goodreads');
  const [showQueue, setShowQueue] = useState(false);
  const steps = [{
    icon: DownloadCloudIcon,
    label: 'Export'
  }, {
    icon: UploadCloudIcon,
    label: 'Upload'
  }, {
    icon: MapIcon,
    label: 'Map & Preview'
  }, {
    icon: LibraryIcon,
    label: 'Add to Bookshelf'
  }];
  const handleImportComplete = () => {
    setShowQueue(true);
    // Scroll to queue
    setTimeout(() => {
      document.getElementById('import-queue')?.scrollIntoView({
        behavior: 'smooth'
      });
    }, 100);
  };
  const handleFinish = () => {
    navigate('/my-bookshelf');
  };
  return <div className={`min-h-screen pb-20 ${dk.page}`} data-id="element-10315" data-mp="21aspleonuzt8">
      {/* Header */}
      <div className={`border-b ${dk.borderSubtle} ${dk.surface}`} data-id="element-10316" data-mp="2yzmapdpu2hzy">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-10317" data-mp="2u2cftijode4q">
          <button onClick={() => navigate('/my-bookshelf')} className={`flex items-center text-sm font-medium mb-6 ${dk.textMuted} hover:${dk.textPrimary} transition-colors`} data-id="element-10318" data-mp="2p1y51sqovq30">
            <ArrowLeftIcon className="w-4 h-4 mr-1" data-id="element-10319" data-mp="1dnkn14ima4uw" />
            Back to Bookshelf
          </button>

          <div className="flex items-center gap-4 mb-4" data-id="element-10320" data-mp="19l7qn6prfjas">
            <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl" data-id="element-10321" data-mp="19ntzd9oe04ul">
              <LibraryIcon className="w-8 h-8 text-indigo-600 dark:text-indigo-400" data-id="element-10322" data-mp="1b3ef6lqvtu92" />
            </div>
            <div data-id="element-10323" data-mp="1g0y3yfl7hpf9">
              <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-10324" data-mp="1fzd90ouzc4gv">
                Bring Your Library With You
              </h1>
              <p className={`text-lg mt-1 ${dk.textSecondary}`} data-id="element-10325" data-mp="162qt8ehig3lp">
                Import your reading history, shelves, and reviews from other
                platforms.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-10326" data-mp="x46hxyea4w7i">
        {/* Lifecycle Band */}
        <div className="mb-12" data-id="element-10327" data-mp="ifsb7cdqsnlo">
          <div className="flex items-center justify-between relative" data-id="element-10328" data-mp="ncskrx9sw8hj">
            <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-full h-0.5 ${dk.borderSubtle} -z-10`} data-id="element-10329" data-mp="nay10ma9mmf1" />
            {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = showQueue ? index >= 2 : index <= 1;
            return <div key={step.label} className="flex flex-col items-center bg-inherit" data-id="element-10330" data-mp="3qxt09mfvoywy" data-mpi={index}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 border-2 ${isActive ? 'border-indigo-500 bg-indigo-50 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-400' : `${dk.border} ${dk.surface} ${dk.textMuted}`}`} data-id="element-10331" data-mp="3sbjm9p3qutr">
                    <Icon className="w-5 h-5" data-id="element-10332" data-mp="8paeiuglb5t0" />
                  </div>
                  <span className={`text-xs font-semibold uppercase tracking-wider ${isActive ? dk.textPrimary : dk.textMuted} bg-inherit px-2`} data-id="element-10333" data-mp="mhi687t0o6ku">
                    {step.label}
                  </span>
                </div>;
          })}
          </div>
        </div>

        {/* Method Selection */}
        <div className="mb-8" data-id="element-10334" data-mp="rio0wb68m3x0">
          <h2 className={`text-xl font-bold mb-4 ${dk.textHeading}`} data-id="element-10335" data-mp="1bixng7ar8kao">
            Select Import Source
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-10336" data-mp="1fk13atssygbw">
            <button onClick={() => {
            setActiveMethod('goodreads');
            setShowQueue(false);
          }} className={`p-4 rounded-xl border-2 text-left transition-all ${activeMethod === 'goodreads' ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-900/10' : `${dk.border} ${dk.card} hover:${dk.bgHover}`}`} data-id="element-10337" data-mp="1fi6idfhj6o7t">
              <div className="flex items-center justify-between mb-2" data-id="element-10338" data-mp="wlymv3eblrpp">
                <span className={`font-bold ${dk.textHeading}`} data-id="element-10339" data-mp="11jqa3k40spnk">Goodreads</span>
                <div className={`w-4 h-4 rounded-full border ${activeMethod === 'goodreads' ? 'border-4 border-indigo-500' : dk.border}`} data-id="element-10340" data-mp="16ke3gcfq1iky" />
              </div>
              <p className={`text-sm ${dk.textSecondary}`} data-id="element-10341" data-mp="2hyi6070dkf1m">
                Import from goodreads_library_export.csv
              </p>
            </button>

            <button onClick={() => {
            setActiveMethod('generic');
            setShowQueue(false);
          }} className={`p-4 rounded-xl border-2 text-left transition-all ${activeMethod === 'generic' ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-900/10' : `${dk.border} ${dk.card} hover:${dk.bgHover}`}`} data-id="element-10342" data-mp="6awlnj2hedui">
              <div className="flex items-center justify-between mb-2" data-id="element-10343" data-mp="3jrc9u65j082u">
                <span className={`font-bold ${dk.textHeading} flex items-center`} data-id="element-10344" data-mp="3eqyadchzq6gf">
                  <UploadCloudIcon className="w-4 h-4 mr-1.5 text-sky-500" data-id="element-10345" data-mp="39tg45tvcy5c4" />
                  Any CSV
                </span>
                <div className={`w-4 h-4 rounded-full border ${activeMethod === 'generic' ? 'border-4 border-indigo-500' : dk.border}`} data-id="element-10346" data-mp="34v4pn5u4b6nk" />
              </div>
              <p className={`text-sm ${dk.textSecondary}`} data-id="element-10347" data-mp="132gkgqxwy955">
                Upload from any platform with smart column mapping
              </p>
            </button>

            <button onClick={() => {
            setActiveMethod('csv');
            setShowQueue(false);
          }} className={`p-4 rounded-xl border-2 text-left transition-all ${activeMethod === 'csv' ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-900/10' : `${dk.border} ${dk.card} hover:${dk.bgHover}`}`} data-id="element-10348" data-mp="3295e9gn2grxy">
              <div className="flex items-center justify-between mb-2" data-id="element-10349" data-mp="3l7q28ztnvh7d">
                <span className={`font-bold ${dk.textHeading} flex items-center`} data-id="element-10350" data-mp="3l6yhkk1lr3vv">
                  <FileSpreadsheetIcon className="w-4 h-4 mr-1.5 text-green-500" data-id="element-10351" data-mp="37ab7dfkn2h80" />
                  Universal CSV
                </span>
                <div className={`w-4 h-4 rounded-full border ${activeMethod === 'csv' ? 'border-4 border-indigo-500' : dk.border}`} data-id="element-10352" data-mp="1vsjx4bxma366" />
              </div>
              <p className={`text-sm ${dk.textSecondary}`} data-id="element-10353" data-mp="1sacpp7aewums">
                Use our standard template
              </p>
            </button>
          </div>
        </div>

        {/* Active Form */}
        <div className="mb-12" data-id="element-10354" data-mp="229lgxsdw7v5g">
          {activeMethod === 'goodreads' && <GoodreadsImportForm onImportComplete={handleImportComplete} data-id="element-10355" data-mp="1x9z3bhu5kcw4" />}
          {activeMethod === 'generic' && <GenericCsvForm onImportComplete={handleImportComplete} data-id="element-10356" data-mp="19jlavq7noc10" />}
          {activeMethod === 'csv' && <UniversalCsvForm onImportComplete={handleImportComplete} data-id="element-10357" data-mp="14mme0zwlxqz4" />}
        </div>

        {/* Preview Queue */}
        {showQueue && <div id="import-queue" className="pt-8 border-t border-gray-200 dark:border-gray-800" data-id="element-10358" data-mp="1ejr59e5b8cms">
            <div className="mb-6" data-id="element-10359" data-mp="qsv6ty6cmj6k">
              <h2 className={`text-2xl font-bold ${dk.textHeading}`} data-id="element-10360" data-mp="qrsgpsguokrp">
                Map & Preview
              </h2>
              <p className={`text-sm mt-1 ${dk.textSecondary}`} data-id="element-10361" data-mp="bh143nujyjq">
                Review how your books matched with our catalog. You can edit
                details or skip books before adding them to your bookshelf.
              </p>
            </div>
            <ImportPreviewQueue onComplete={handleFinish} data-id="element-10362" data-mp="a8vj47pf4ve5" />
          </div>}
      </div>
    </div>;
}